import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, notificationsAPI } from '../services/api';
import { requestForToken } from '../firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const registerPushNotifications = async () => {
    try {
      const token = await requestForToken();
      if (token) {
        await notificationsAPI.registerFCMToken(token);
        console.log('FCM Token registered successfully');
      }
    } catch (error) {
      console.error('Failed to register push notifications:', error);
    }
  };

  useEffect(() => {
    // Check for existing token in either localStorage or sessionStorage
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      // Set the token temporarily in axios header via interceptor (handled in api.js)
      // or just trust that api.js reads from localStorage. 
      // NOTE: Our api.js currently reads from localStorage.ITEM('token')
      // Let's ensure api.js is flexible or we sync them here.

      authAPI.getMe()
        .then(response => {
          const userData = response.data;
          setUser({ ...userData, role: userData.role?.toLowerCase() });
          registerPushNotifications(); // Register token on app load if logged in
        })
        .catch(() => {
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password, rememberMe = false) => {
    try {
      const response = await authAPI.login({ username, password });
      const { access_token, user: userData } = response.data;

      if (rememberMe) {
        localStorage.setItem('token', access_token);
        sessionStorage.removeItem('token'); // Clear session if persistent
      } else {
        sessionStorage.setItem('token', access_token);
        localStorage.removeItem('token'); // Clear persistent if session
      }

      const normalizedUser = { ...userData, role: userData.role?.toLowerCase() };
      setUser(normalizedUser);
      registerPushNotifications(); // Register token on login
      return normalizedUser;
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = (data) => {
    const role = data.role ? data.role.toLowerCase() : undefined;
    setUser(prev => ({ ...prev, ...data, ...(role && { role }) }));
  };

  const signup = async (signupData) => {
    try {
      const response = await authAPI.signup(signupData);
      if (response.data.message === "VERIFICATION_EMAIL_SENT") {
        return { verificationRequired: true };
      }

      const { access_token, user: userData } = response.data;
      if (access_token) {
        // Signup typically defaults to persistent or we can choose
        localStorage.setItem('token', access_token);
        const normalizedUser = { ...userData, role: userData.role?.toLowerCase() };
        setUser(normalizedUser);
        return normalizedUser;
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
